import type { PostHog } from 'posthog-js'

/**
 * Who gets session replay. PostHog feature flags in the prod project, targeted
 * on person properties the app already sets:
 *
 * - `replay-backtester`: `first_visited_backtest_at` in the last 14 days.
 * - `replay-new-users`: `signed_up_at` in the last 2 days (set by the register
 *   page on a successful signup).
 *
 * Both exclude @edgetotrade.com. Replay stays disabled at init
 * (`disable_session_recording: true`), so these flags are the only way a
 * recording starts: if the project's trigger settings were ever widened, the
 * audience still could not grow past these two cohorts. Which pages are kept
 * (e.g. only /backtest for the backtester cohort) is the project's trigger
 * groups' job, which is why the start below passes no overrides.
 *
 * Users outside both flags never download the recorder: posthog-js loads it
 * lazily on the first start.
 */
export const REPLAY_FLAGS = ['replay-backtester', 'replay-new-users'] as const

type ReplayClient = Pick<
  PostHog,
  'isFeatureEnabled' | 'sessionRecordingStarted' | 'startSessionRecording' | 'stopSessionRecording'
>

/**
 * Start or stop replay to match the replay flags. Called on every flag load,
 * so identify (flags now evaluate for the person) and logout (reset() drops
 * back to an anonymous person with no flags) both settle it.
 */
export function syncSessionReplay(client: ReplayClient): void {
  // send_event: false: this runs on every flag reload, and each
  // $feature_flag_called event is billed.
  const wanted = REPLAY_FLAGS.some(
    (flag) => client.isFeatureEnabled(flag, { send_event: false }) === true,
  )
  const running = client.sessionRecordingStarted()
  if (wanted && !running) client.startSessionRecording()
  else if (!wanted && running) client.stopSessionRecording()
}
